// Visuals for the street layer: procedural pedestrians (capsule people), traffic and police cars from the
// vehicle library, glowing traffic-light lamps, and the disaster effects (wave, tornado, debris, rocks).

import { Scene } from '@babylonjs/core/scene';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { ASSET_BASE } from './config';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import { VehicleLibrary, type VehicleView } from './vehicle';
import { PeopleLibrary, type PersonView, PEOPLE_VARIANTS, PALETTES } from './people';
import type { Streets } from '../sim/streets';
import type { DisasterDirector } from '../sim/disasters';
import type { Ground } from '../sim/ground';

const PED_COLOURS = ['#E5433A', '#3E8BFF', '#4CC26B', '#F2B90C', '#9B5DE5', '#FF7A1A', '#FF6BB5', '#2ABF9E'];
const CIVILIAN_MODELS = Array.from({ length: 15 }, (_, i) => `SM_Vehicles_${String(i + 1).padStart(2, '0')}_V1`);

export class StreetFx {
  private peds = new Map<number, PersonView>();
  private cars = new Map<number, VehicleView>();
  private police = new Map<number, VehicleView>();
  private lampRed: Mesh; private lampGreen: Mesh;
  private lamps: { ns: InstancedMesh[]; ew: InstancedMesh[]; inter: number }[] = [];
  // disasters
  private wave: Mesh; private tornado: Mesh; private dust: Mesh;
  private ringProto: Mesh; private rockProto: Mesh; private debrisProto: Mesh; private fireProto: Mesh;
  private disasterTexture: Texture;
  private disasterMaterials: StandardMaterial[] = [];
  private rings = new Map<string, InstancedMesh>(); private rocks = new Map<string, InstancedMesh>(); private fires = new Map<string, InstancedMesh>();

  constructor(readonly scene: Scene, readonly ground: Ground, readonly vehicles: VehicleLibrary, readonly people: PeopleLibrary | null, readonly shadows: CascadedShadowGenerator, streets: Streets) {
    // pedestrians: body capsule + head sphere, one prototype per colour (fallback when the people pack is missing)
    for (const c of PED_COLOURS) {
      const body = MeshBuilder.CreateCapsule(`ped_${c}`, { height: 1.2, radius: 0.28, tessellation: 8, subdivisions: 1 }, scene);
      const head = MeshBuilder.CreateSphere(`pedhead_${c}`, { diameter: 0.42, segments: 8 }, scene);
      head.position.y = 0.85;
      const mat = new StandardMaterial(`pedmat_${c}`, scene); mat.diffuseColor = Color3.FromHexString(c); mat.specularColor = Color3.Black();
      const skin = new StandardMaterial(`skin_${c}`, scene); skin.diffuseColor = new Color3(1, 0.85, 0.7); skin.specularColor = Color3.Black();
      body.material = mat; head.material = skin;
      const merged = Mesh.MergeMeshes([body, head], true, true, undefined, false, true)!;
      merged.name = `ped_${c}`; merged.isVisible = false; merged.isPickable = false; merged.position.y = 0.6;
      this.pedProto.push(merged);
    }
    // traffic light lamps
    const mk = (n: string, c: Color3) => { const m = MeshBuilder.CreateSphere(n, { diameter: 0.5, segments: 6 }, scene); const mat = new StandardMaterial(n, scene); mat.emissiveColor = c; mat.diffuseColor = c; mat.disableLighting = true; m.material = mat; m.isVisible = false; m.isPickable = false; return m; };
    this.lampRed = mk('lampRed', new Color3(1, 0.15, 0.1)); this.lampGreen = mk('lampGreen', new Color3(0.1, 1, 0.3));
    for (const inter of streets.intersections) {
      const ns: InstancedMesh[] = [], ew: InstancedMesh[] = [];
      for (const l of inter.lights) {
        // the lamp head hangs ~4.6 m up, out along the arm (yaw points along the arm)
        const yaw = l.yaw * Math.PI / 180; const ax = Math.sin(yaw), az = Math.cos(yaw);
        const lx = l.x + ax * 2.6, lz = l.z + az * 2.6, ly = l.y + 4.7;
        const facesNs = Math.abs(Math.cos(yaw)) < Math.abs(Math.sin(yaw)); // arm sticks out over the road it controls
        const r = this.lampRed.createInstance('r'); r.position.set(lx, ly, lz); const g = this.lampGreen.createInstance('g'); g.position.set(lx, ly - 0.55, lz);
        (facesNs ? ns : ew).push(r, g);
      }
      this.lamps.push({ ns, ew, inter: inter.id });
    }
    // disasters
    // Repeat the foam at street scale rather than stretching one image across the city.
    const waveSections: Mesh[] = [];
    for (let i = 0; i < 50; i++) {
      const section = MeshBuilder.CreateBox('waveSection', { width: 14, height: 5, depth: 14 }, scene);
      section.position.z = -343 + i * 14; waveSections.push(section);
    }
    this.wave = Mesh.MergeMeshes(waveSections, true)!; this.wave.name = 'wave';
    const wm = new StandardMaterial('waveMat', scene); wm.diffuseColor = new Color3(0.2, 0.6, 0.95); wm.emissiveColor = new Color3(0.05, 0.2, 0.4); wm.alpha = 0.75; this.wave.material = wm; this.wave.setEnabled(false); this.wave.isPickable = false;
    this.tornado = MeshBuilder.CreateCylinder('tornado', { diameterTop: 36, diameterBottom: 4, height: 60, tessellation: 12 }, scene);
    const tm = new StandardMaterial('tornadoMat', scene); tm.diffuseColor = new Color3(0.45, 0.45, 0.5); tm.alpha = 0.55; tm.backFaceCulling = false; this.tornado.material = tm; this.tornado.setEnabled(false); this.tornado.isPickable = false;
    this.dust = MeshBuilder.CreateDisc('dust', { radius: 14, tessellation: 24 }, scene); this.dust.rotation.x = Math.PI / 2; const dm = new StandardMaterial('dustMat', scene); dm.diffuseColor = new Color3(0.6, 0.55, 0.45); dm.alpha = 0.5; this.dust.material = dm; this.dust.setEnabled(false);
    this.ringProto = MeshBuilder.CreateTorus('targetRing', { diameter: 9, thickness: 0.6, tessellation: 24 }, scene); const rm = new StandardMaterial('ringMat', scene); rm.emissiveColor = new Color3(1, 0.2, 0.1); rm.disableLighting = true; this.ringProto.material = rm; this.ringProto.isVisible = false;
    this.rockProto = MeshBuilder.CreateSphere('rock', { diameter: 2.6, segments: 5 }, scene); const km = new StandardMaterial('rockMat', scene); km.diffuseColor = new Color3(0.25, 0.22, 0.2); km.emissiveColor = new Color3(0.6, 0.15, 0); this.rockProto.material = km; this.rockProto.isVisible = false;
    this.fireProto = MeshBuilder.CreateDisc('fire', { radius: 3.2, tessellation: 16 }, scene); this.fireProto.rotation.x = Math.PI / 2; const fm = new StandardMaterial('fireMat', scene); fm.emissiveColor = new Color3(1, 0.45, 0.05); fm.disableLighting = true; fm.alpha = 0.8; this.fireProto.material = fm; this.fireProto.isVisible = false;
    this.debrisProto = MeshBuilder.CreateSphere('debris', { diameter: 2.6, segments: 5 }, scene);
    this.debrisProto.isVisible = false;
    const debrisMat = new StandardMaterial('debrisMat', scene); this.debrisProto.material = debrisMat;
    this.disasterTexture = new Texture(`${ASSET_BASE}textures/disasters.png`, scene, false, true, Texture.BILINEAR_SAMPLINGMODE);
    // UVs select padded atlas quadrants. All five materials share one GPU texture.
    const texture = (mesh: Mesh, mat: StandardMaterial, col: number, row: number, glow = 0.15) => {
      const uv = mesh.getVerticesData(VertexBuffer.UVKind);
      if (uv) {
        for (let i = 0; i < uv.length; i += 2) {
          uv[i] = col * .5 + .002 + uv[i] * .496;
          uv[i + 1] = (1 - row) * .5 + .002 + uv[i + 1] * .496;
        }
        mesh.setVerticesData(VertexBuffer.UVKind, uv);
      }
      mat.diffuseColor = Color3.White(); mat.emissiveColor = new Color3(glow, glow, glow);
      mat.diffuseTexture = this.disasterTexture; mat.emissiveTexture = this.disasterTexture;
      mat.specularColor = Color3.Black(); this.disasterMaterials.push(mat);
    };
    texture(this.wave, wm, 0, 0, .3); wm.alpha = .87;
    texture(this.tornado, tm, 1, 0, .2); tm.alpha = .82;
    texture(this.dust, dm, 1, 0);
    texture(this.rockProto, km, 0, 1, .65);
    texture(this.fireProto, fm, 0, 1, 1);
    texture(this.debrisProto, debrisMat, 1, 1);

  }

  private pedProto: Mesh[] = [];
  private fallbackPeds = new Map<number, TransformNode>();

  /** People beyond this distance from the focus are hidden and their animation paused. */
  peopleRange = 90;

  update(streets: Streets, director: DisasterDirector, time: number, saintFor: { x: number; z: number; yaw: number } | null, dt: number, focus?: { x: number; z: number }): void {
    // pedestrians
    const seenP = new Set<number>(); let spawned = 0;
    for (const p of streets.peds) {
      if (p.state === 'gone') continue;
      seenP.add(p.id);
      if (this.people) {
        let v = this.peds.get(p.id);
        if (!v) { if (spawned >= 3) continue; spawned++; v = this.people.spawn(p.id * 7 % PEOPLE_VARIANTS.length, p.colour % PALETTES, null); this.peds.set(p.id, v); }
        const far = focus ? Math.hypot(p.x - focus.x, p.z - focus.z) > this.peopleRange : false;
        v.cull(far); if (far) continue;
        if (p.state === 'fly') {
          // airborne: spin about the middle (the model pivots at the feet, so lift it); landed: lie flat on the ground
          const landed = p.vy === 0 && p.y <= this.ground.heightAt(p.x, p.z) + 0.05;
          if (landed) { v.place(p.x, p.y + 0.12, p.z, p.yaw); v.lie(); }
          else { v.place(p.x, p.y + 0.9, p.z, p.yaw); v.stopClips(); v.tumble(dt); }
        }
        else { v.place(p.x, p.y, p.z, p.yaw); v.resetPose(); if (!v.play('Walk', 0.7 + p.speed * 0.6)) v.walk(time, p.speed); }
      } else {
        let n = this.fallbackPeds.get(p.id);
        if (!n) { n = this.pedProto[p.colour % this.pedProto.length].createInstance(`p${p.id}`); this.fallbackPeds.set(p.id, n); }
        n.position.set(p.x, p.y + 0.6, p.z);
        if (p.state === 'fly') { if (p.vy === 0 && p.y <= this.ground.heightAt(p.x, p.z) + 0.05) { n.rotation.set(Math.PI / 2, p.yaw, 0); n.position.y = p.y + 0.3; } else { n.rotation.x += dt * 9; n.rotation.z += dt * 5; } }
        else { n.rotation.set(0, p.yaw, 0); n.position.y += Math.abs(Math.sin(time * 9 + p.id)) * 0.08; }
      }
    }
    for (const [id, v] of this.peds) if (!seenP.has(id)) { v.dispose(); this.peds.delete(id); }
    for (const [id, n] of this.fallbackPeds) if (!seenP.has(id)) { n.dispose(); this.fallbackPeds.delete(id); }
    // traffic
    const seenC = new Set<number>();
    for (const t of streets.traffic) {
      seenC.add(t.id);
      let v = this.cars.get(t.id);
      if (!v) { v = this.vehicles.spawn(CIVILIAN_MODELS[t.model % CIVILIAN_MODELS.length], null, this.shadows); this.cars.set(t.id, v); }
      v.apply(t.car);
    }
    for (const [id, v] of this.cars) if (!seenC.has(id)) { v.dispose(); this.cars.delete(id); }
    const seenPo = new Set<number>();
    for (const c of streets.police) {
      seenPo.add(c.id);
      let v = this.police.get(c.id);
      if (!v) { v = this.vehicles.spawn('SM_Vehicles_Police_01', null, this.shadows); this.police.set(c.id, v); }
      v.apply(c.car);
    }
    for (const [id, v] of this.police) if (!seenPo.has(id)) { v.dispose(); this.police.delete(id); }
    // lights
    for (const l of this.lamps) {
      const inter = streets.intersections[l.inter];
      const ph = streets.phaseAt(inter, time, saintFor);
      const nsGreen = ph.green === 'ns' && !ph.amber, ewGreen = ph.green === 'ew' && !ph.amber;
      for (let i = 0; i < l.ns.length; i += 2) { l.ns[i].setEnabled(!nsGreen); l.ns[i + 1].setEnabled(nsGreen); }
      for (let i = 0; i < l.ew.length; i += 2) { l.ew[i].setEnabled(!ewGreen); l.ew[i + 1].setEnabled(ewGreen); }
    }
    // disasters
    let waveOn = false, tornadoOn = false;
    const liveRings = new Set<string>(), liveRocks = new Set<string>(), liveFires = new Set<string>();
    for (const a of director.active) {
      const t0 = a.spec.start + a.spec.warn, t1 = t0 + a.spec.duration;
      if (time < t0) continue;
      if (a.spec.kind === 'tsunami' && time < t1) {
        waveOn = true; const front = director.tsunamiFront(a, time);
        this.wave.position.set(front - 7, 1.5 + Math.sin(time * 3) * 0.3, (director.bounds.z[0] + director.bounds.z[1]) / 2);
      } else if (a.spec.kind === 'cyclone' && time < t1) {
        tornadoOn = true; const c = director.cyclonePos(a, time);
        this.tornado.position.set(c.x, this.ground.heightAt(c.x, c.z) + 30, c.z); this.tornado.rotation.y += dt * 6;
        this.dust.position.set(c.x, this.ground.heightAt(c.x, c.z) + 0.3, c.z); this.dust.rotation.y -= dt * 3;
      } else if (a.spec.kind === 'earthquake' || a.spec.kind === 'volcano') {
        for (const im of a.impactors) {
          const key = `${a.spec.id}:${im.id}`;
          if (time < im.landAt) {
            liveRings.add(key);
            let r = this.rings.get(key); if (!r) { r = this.ringProto.createInstance(key); r.position.set(im.x, this.ground.heightAt(im.x, im.z) + 0.2, im.z); this.rings.set(key, r); }
            const f = 1 - (im.landAt - time) / (a.spec.kind === 'earthquake' ? 2 : 2.5); r.scaling.setAll(1.4 - f * 0.6);
            liveRocks.add(key);
            let k = this.rocks.get(key); if (!k) { k = (im.kind === 'debris' ? this.debrisProto : this.rockProto).createInstance('k' + key); this.rocks.set(key, k); }
            if (a.spec.kind === 'volcano') {
              // arc from the volcano to the target
              const v = director.volcano; const u = f; const y = (1 - u) * v[1] + u * (this.ground.heightAt(im.x, im.z) + 1) + Math.sin(u * Math.PI) * 120;
              k.position.set(v[0] + (im.x - v[0]) * u, y, v[2] + (im.z - v[2]) * u);
            } else k.position.set(im.x, this.ground.heightAt(im.x, im.z) + 1 + (1 - f) * 40, im.z);
            k.rotation.x += dt * 3; k.rotation.z += dt * 2;
          } else if (im.fireUntil && time < im.fireUntil) {
            liveFires.add(key);
            let fi = this.fires.get(key); if (!fi) { fi = this.fireProto.createInstance('f' + key); fi.position.set(im.x, this.ground.heightAt(im.x, im.z) + 0.15, im.z); this.fires.set(key, fi); }
            fi.scaling.setAll(0.9 + Math.sin(time * 12 + im.id) * 0.1);
          }
        }
      }
    }
    this.wave.setEnabled(waveOn); this.tornado.setEnabled(tornadoOn); this.dust.setEnabled(tornadoOn);
    for (const [k, m] of this.rings) if (!liveRings.has(k)) { m.dispose(); this.rings.delete(k); }
    for (const [k, m] of this.rocks) if (!liveRocks.has(k)) { m.dispose(); this.rocks.delete(k); }
    for (const [k, m] of this.fires) if (!liveFires.has(k)) { m.dispose(); this.fires.delete(k); }
  }

  dispose(): void {
    for (const v of this.peds.values()) v.dispose(); for (const n of this.fallbackPeds.values()) n.dispose(); for (const v of this.cars.values()) v.dispose(); for (const v of this.police.values()) v.dispose();
    for (const l of this.lamps) { for (const m of [...l.ns, ...l.ew]) m.dispose(); }
    this.wave.dispose(); this.tornado.dispose(); this.dust.dispose();
    this.rockProto.dispose(); this.debrisProto.dispose(); this.fireProto.dispose(); this.ringProto.dispose();
    for (const mat of this.disasterMaterials) mat.dispose();
    this.disasterTexture.dispose();
    for (const m of [...this.rings.values(), ...this.rocks.values(), ...this.fires.values()]) m.dispose();
  }
}

export function vec(x: number, y: number, z: number): Vector3 { return new Vector3(x, y, z); }
